import React, { useState, Fragment } from 'react';
import { SaveButton } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { ArchiveButton, DeleteButton } from '~/components';
import cx from 'classnames';
import BlockButton from '~/components/BlockButton';
import { useFormState } from 'react-final-form';
import { isEqual } from 'lodash';
import { cleanupFormValues } from '~/utils';
import { ConfirmationSettings, ConfirmationBase } from '~/components/Confirmation';
import Toolbar from '../Toolbar';
import { useDebouncedCallback } from 'use-debounce/lib';

export interface BottomToolbarBaseProps<Record> {
  disabled?: boolean;
  withDelete?: boolean;
  deleteButtonProps?: {
    variant: 'delete' | 'block' | 'close' | 'archive' | 'unarchive';
    [x: string]: any;
  };
  saveButtonProps?: {
    color: 'default' | 'red';
    [x: string]: any;
  };
  classes?: {
    bottomToolbar: string;
  };
  externalState?: Record;
  withSaveConfirmation?: boolean;
  withDeleteConfirmation?: boolean;
  saveConfirmationSettings?: ConfirmationSettings<Record>;
  deleteConfirmationSettings?: ConfirmationSettings<Record>;
  toolbarProps?: { [x: string]: any };
}

interface BottomToolbarProps<Record = any> extends BottomToolbarBaseProps<Record> {
  onSave: (newRecord: Record) => void;
  onUnarchive: (newRecord: Record) => void;
  onDelete: () => void;
  deletionLoading?: boolean;
  // Props ejected by react-admin
  [x: string]: any;
}

function BottomToolbar<Record>(props: BottomToolbarProps<Record>) {
  const classes = useStyles();
  const {
    onSave,
    onDelete,
    onUnarchive,
    withDelete,
    deleteButtonProps = {
      variant: 'delete',
    },
    saveButtonProps = {
      color: 'default',
    },
    disabled,
    deletionLoading,
    classes: cssApi = {
      bottomToolbar: '',
    },
    externalState,
    record,
    saveConfirmationSettings,
    deleteConfirmationSettings,
    withSaveConfirmation,
    withDeleteConfirmation,
    toolbarProps,
    // Props ejected by react-admin
    ...rest
  } = props;

  const handleSave = useDebouncedCallback(onSave, 100);

  const formState = useFormState<Record>();
  const prevRecord = formState.initialValues;
  const newRecord: any = formState.values;

  const { color: saveButtonColor, ...saveButtonPropsRest } = saveButtonProps;

  const isPrestine = () => {
    if (externalState) {
      return isEqual(cleanupFormValues(record, 'hard'), cleanupFormValues(externalState, 'hard'));
    } else {
      return isEqual(cleanupFormValues(prevRecord, 'hard'), cleanupFormValues(newRecord, 'hard'));
    }
  };

  // Confirmation modal or drawer toggler
  const [openSaveConfirmation, setSaveOpen] = useState(false);
  const handleSaveOpen = () => setSaveOpen(true);
  const handleSaveClose = () => setSaveOpen(false);

  const [opeDeleteConfirmation, setDeleteOpen] = useState(false);
  const hanDeleteOpen = () => setDeleteOpen(true);
  const hanDeleteClose = () => setDeleteOpen(false);

  const handleDelete = withDeleteConfirmation ? hanDeleteOpen : onDelete;

  return (
    <Fragment>
      <ConfirmationBase<Record>
        open={openSaveConfirmation}
        handleClose={handleSaveClose}
        onConfirm={() => handleSave(formState.values)}
        confirmationSettings={saveConfirmationSettings}
        prevRecord={prevRecord}
        newRecord={newRecord}
      />
      <ConfirmationBase<Record>
        open={opeDeleteConfirmation}
        handleClose={hanDeleteClose}
        onConfirm={onDelete}
        confirmationSettings={deleteConfirmationSettings}
        prevRecord={prevRecord}
        newRecord={newRecord}
      />
      <Toolbar
        className={cx(classes.toolbar, { [cssApi.bottomToolbar]: cssApi.bottomToolbar })}
        {...toolbarProps}
        {...rest}
      >
        <SaveButton
          className={cx({ [classes.red]: saveButtonColor === 'red' })}
          disabled={disabled || isPrestine()}
          onSave={withSaveConfirmation ? handleSaveOpen : handleSave}
          {...saveButtonPropsRest}
        />
        {(() => {
          const { variant, ...rest } = deleteButtonProps;
          if (withDelete) {
            switch (variant) {
              case 'delete':
                return (
                  <DeleteButton
                    disabled={disabled}
                    onClick={handleDelete}
                    loading={deletionLoading}
                    {...rest}
                  />
                );
              case 'block':
                return (
                  <BlockButton
                    disabled={disabled}
                    onClick={handleDelete}
                    loading={deletionLoading}
                    red
                    {...rest}
                  />
                );
              case 'close':
                return (
                  <BlockButton
                    disabled={disabled}
                    label='close'
                    onClick={handleDelete}
                    loading={deletionLoading}
                    red
                    {...rest}
                  />
                );
              case 'archive':
                return (
                  <ArchiveButton
                    disabled={disabled}
                    label='archive'
                    onClick={handleDelete}
                    loading={deletionLoading}
                    red
                    {...rest}
                  />
                );
              case 'unarchive':
                return (
                  <ArchiveButton
                    disabled={disabled}
                    label='unarchive'
                    onClick={onUnarchive}
                    loading={deletionLoading}
                    red
                    unarchive
                    {...rest}
                  />
                );
              default:
                return null;
            }
          } else return null;
        })()}
      </Toolbar>
    </Fragment>
  );
}

const useStyles = makeStyles((theme) => ({
  toolbar: {
    justifyContent: 'space-between',
  },
  red: {
    backgroundColor: theme.palette.error.main,
  },
}));

export default BottomToolbar;
